'use client'

/**
 * Application-frame context (Issue #12946)
 *
 * Holds the pieces of state the frame's layout needs, and nothing else:
 *
 *   - `sidecarWidth` — how much horizontal space the application pane must
 *     give up right now. Reserved by whoever renders into the sidecar slot
 *     (today: `AiChatPanel`, and only on desktop). 0 means "no sidecar", which
 *     is the state a chat-disabled org, a hidden route, presentation mode and
 *     every mobile viewport are all in — so those layouts stay byte-identical
 *     to the pre-#12946 full-width one.
 *   - `fabClearance` — how much of the BOTTOM of the browser viewport a
 *     viewport-pinned control occupies right now, in px. The vertical twin of
 *     `sidecarWidth`, added by #13731 and reserved by whoever renders the
 *     control (today: `AiChatFab`). 0 means "nothing is pinned to the bottom",
 *     which is every route where `shouldHideAiChat` suppresses the FAB — so
 *     those layouts stay byte-identical to the pre-#13731 ones.
 *   - `paneRef` / `paneWidth` — the application pane and its OBSERVED inline
 *     size. Not `window.innerWidth`: between ~640px and ~1080px of browser
 *     width, an open sidecar makes the pane narrower than the `sm` breakpoint
 *     while the window is not, and a page that reads the window gets the wrong
 *     answer. The element is a REF and only the width is state — see
 *     `registerPane` below for the update loop that distinction prevents. The
 *     width rides in its own context (`AppPaneWidthContext`) so a resize tick
 *     does not re-render the reservation consumers.
 *   - `queryContainerRequests` — the ref count behind the pane's
 *     `container-type`. See `useAppPaneQueryContainer` for why that is opt-in.
 *
 * Provider placement is the root layout, above both the route tree and the
 * chat. Everything here fails open when no provider is mounted (unit tests,
 * isolated component mounts): the reservation is a no-op, the width is `null`.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'

/**
 * The CSS `container-name` carried by the application pane.
 *
 * Tailwind v4 spells a query against it as `@min-[900px]/app-pane:…`; plain
 * CSS spells it `@container app-pane (min-width: 900px)`. Exported so a
 * migrating route can assert the name it queries against rather than
 * hardcoding a string that drifts.
 */
export const APP_PANE_CONTAINER_NAME = 'app-pane'

interface AppFrameContextValue {
  sidecarWidth: number
  reserveSidecar: (width: number) => void
  fabClearance: number
  reserveFabClearance: (px: number) => void
  /** Measured recording bar height, bottom offset, and content gap. */
  recordingBarClearance: number
  reserveRecordingBarClearance: (px: number) => void
  paneRef: RefObject<HTMLElement | null>
  /**
   * Callback ref for the pane element. Returns a cleanup function (React 19
   * ref-callback cleanup), so React never calls it back with `null`.
   *
   * This deliberately writes the element to a REF, never to state. Storing it
   * in state is a self-amplifying loop: a subtree that remounts whenever the
   * frame's context value changes — an error boundary retrying, a Suspense
   * boundary re-suspending, both of which happen during a failed DB read —
   * hands React a BRAND NEW node on every pass, and a new node never equals
   * the previous one, so `setState` can never bail out. Observed as
   * "Maximum update depth exceeded" at this component during a 503 storm on
   * the dev server; pinned by the "remounting pane" case in
   * tests/unit/components/app-frame/app-frame.test.tsx.
   *
   * The measured WIDTH is state, and that is safe for exactly the reason the
   * element is not: re-measuring a remounted pane yields the same NUMBER, so
   * the update bails out and the cascade terminates.
   */
  registerPane: (element: HTMLElement | null) => (() => void) | undefined
  isQueryContainerEnabled: boolean
  addQueryContainerRequest: () => void
  removeQueryContainerRequest: () => void
}

const DETACHED_PANE_REF: RefObject<HTMLElement | null> = { current: null }

/**
 * Fail-open default. A component rendered without the frame above it (every
 * `render(<Thing />)` in the unit suite) must behave exactly as it did before
 * the frame existed, not throw. Mirrors `AI_CHAT_FAIL_OPEN_DEFAULT` in
 * `ai-chat-provider.tsx`.
 */
const APP_FRAME_FAIL_OPEN: AppFrameContextValue = {
  sidecarWidth: 0,
  reserveSidecar: () => {},
  fabClearance: 0,
  reserveFabClearance: () => {},
  recordingBarClearance: 0,
  reserveRecordingBarClearance: () => {},
  paneRef: DETACHED_PANE_REF,
  registerPane: () => undefined,
  isQueryContainerEnabled: false,
  addQueryContainerRequest: () => {},
  removeQueryContainerRequest: () => {},
}

const AppFrameContext = createContext<AppFrameContextValue>(APP_FRAME_FAIL_OPEN)

/**
 * The observed pane width rides in a SEPARATE context, deliberately.
 *
 * It updates on every `ResizeObserver` tick — a window drag emits one per
 * animation frame — while `sidecarWidth` changes only when something docks or
 * undocks. Carrying both in one value would re-render every reservation
 * consumer (`AiChatPanel`, `AiChatFab`) on each of those ticks, for a number
 * neither one reads. Two contexts means a subscriber pays only for the channel
 * it actually reads.
 */
const AppPaneWidthContext = createContext<number | null>(null)

export function useAppFrame(): AppFrameContextValue {
  return useContext(AppFrameContext)
}

export function AppFrameProvider({ children }: { children: ReactNode }) {
  const [sidecarWidth, setSidecarWidth] = useState(0)
  const [fabClearance, setFabClearance] = useState(0)
  const [recordingBarClearance, setRecordingBarClearance] = useState(0)
  const paneRef = useRef<HTMLElement | null>(null)
  const [paneWidth, setPaneWidth] = useState<number | null>(null)
  const [queryContainerRequests, setQueryContainerRequests] = useState(0)

  const reserveSidecar = useCallback((width: number) => {
    setSidecarWidth(previous => (previous === width ? previous : width))
  }, [])

  const reserveFabClearance = useCallback((px: number) => {
    setFabClearance(previous => (previous === px ? previous : px))
  }, [])

  const reserveRecordingBarClearance = useCallback((px: number) => {
    setRecordingBarClearance(previous => (previous === px ? previous : px))
  }, [])

  const addQueryContainerRequest = useCallback(() => {
    setQueryContainerRequests(count => count + 1)
  }, [])

  const removeQueryContainerRequest = useCallback(() => {
    setQueryContainerRequests(count => Math.max(0, count - 1))
  }, [])

  // Measure the pane, not the window. `getBoundingClientRect().width` rather
  // than `clientWidth` because the pane can be sub-pixel once the sidecar
  // takes a fixed 440px out of a fractional viewport width, and a page
  // choosing a layout at a breakpoint boundary should not oscillate on a
  // rounding error.
  //
  // Attaching happens in a callback ref rather than in an effect keyed on the
  // element, because keeping the element out of React state is the whole
  // safety property here — see `registerPane` on the context type.
  const registerPane = useCallback((element: HTMLElement | null) => {
    // React 19 does not call a cleanup-returning ref back with `null`, but the
    // type still permits it and a non-19 caller (a test rendering AppFrame
    // directly) may. Detaching is the cleanup's job, so there is nothing to do.
    if (!element) return undefined
    paneRef.current = element

    const measure = () => {
      const next = element.getBoundingClientRect().width
      setPaneWidth(previous => (previous === next ? previous : next))
    }

    measure()

    // happy-dom and older Safari both need the fallback; `ResizeObserver` is
    // the only thing that catches a pane resize with no window resize behind
    // it, which is exactly what docking the sidecar is.
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => {
        window.removeEventListener('resize', measure)
        if (paneRef.current === element) paneRef.current = null
      }
    }

    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => {
      observer.disconnect()
      if (paneRef.current === element) paneRef.current = null
    }
  }, [])

  const value = useMemo<AppFrameContextValue>(
    () => ({
      sidecarWidth,
      reserveSidecar,
      fabClearance,
      reserveFabClearance,
      recordingBarClearance,
      reserveRecordingBarClearance,
      paneRef,
      registerPane,
      isQueryContainerEnabled: queryContainerRequests > 0,
      addQueryContainerRequest,
      removeQueryContainerRequest,
    }),
    [
      sidecarWidth,
      reserveSidecar,
      fabClearance,
      reserveFabClearance,
      recordingBarClearance,
      reserveRecordingBarClearance,
      registerPane,
      queryContainerRequests,
      addQueryContainerRequest,
      removeQueryContainerRequest,
    ],
  )

  return (
    <AppFrameContext value={value}>
      <AppPaneWidthContext value={paneWidth}>{children}</AppPaneWidthContext>
    </AppFrameContext>
  )
}

/**
 * Read the currently reserved sidecar width in px (0 when nothing is docked).
 *
 * Use this for viewport-pinned controls that must not sit on top of the
 * sidecar — `AiChatFab` is the first caller.
 */
export function useSidecarWidth(): number {
  return useAppFrame().sidecarWidth
}

/**
 * Reserve `width` px of the frame for a docked sidecar, releasing it on
 * unmount.
 *
 * Single-owner by design: the frame tracks one number, not a stack. Passing 0
 * releases the reservation without unmounting, which is what the chat panel
 * does on mobile (where it is a full-screen dialog and must consume no track)
 * and on hidden/presentation routes.
 */
export function useSidecarReservation(width: number): void {
  const { reserveSidecar } = useAppFrame()

  useEffect(() => {
    reserveSidecar(width)
    return () => reserveSidecar(0)
  }, [width, reserveSidecar])
}

/**
 * Reserve `px` of the bottom of the browser viewport for a pinned control,
 * releasing it on unmount (#13731).
 *
 * The vertical twin of `useSidecarReservation`, and single-owner for the same
 * reason: the frame tracks one number, not a stack. Passing 0 releases the
 * reservation without unmounting, which is what `AiChatFab` does on every
 * route where `shouldHideAiChat` suppresses it — otherwise a FAB-suppressed
 * route would pay 80px of dead space at the end of its scroll area.
 *
 * **Call this above any early return.** `AiChatFab` returns `null` on
 * suppressed routes, and the suppression is exactly the input this hook reads,
 * so a call placed after that return is a CONDITIONAL hook that tears the
 * component down on the first route transition flipping visibility (#3161).
 * Compute the boolean, call the hook with `hidden ? 0 : PX`, then return.
 *
 * There is deliberately no `useFabClearance()` READER to pair with this, the
 * way `useSidecarWidth` pairs with `useSidecarReservation`. The reservation
 * exists to be consumed from CSS — `var(--ai-fab-clearance, 0px)` via
 * `.ai-fab-clear-bottom` — which costs no subscription and no re-render, and
 * a reader hook nobody calls is dead code the unused-export guard is right to
 * reject. Add one when a decision genuinely cannot be expressed in CSS.
 */
export function useFabReservation(px: number): void {
  const { reserveFabClearance } = useAppFrame()

  useEffect(() => {
    reserveFabClearance(px)
    return () => reserveFabClearance(0)
  }, [px, reserveFabClearance])
}

/**
 * A ref to the application pane element, for the rare behaviour that needs the
 * node itself (a scroll position, an `IntersectionObserver` root) rather than
 * its width.
 *
 * A REF and not a state value, deliberately: publishing the node through state
 * makes ref re-attachment schedule a render, and a remounted subtree hands
 * React a new node every pass, which never bails out. Read it inside an effect
 * or an event handler, never during render — during render it may still be
 * `null` on the first pass. `useAppPaneWidth()` is the reactive half.
 */
export function useAppPaneRef(): RefObject<HTMLElement | null> {
  return useAppFrame().paneRef
}

/**
 * The observed inline size of the application pane in px, or `null` before the
 * first measurement (and on the server).
 *
 * This is the JS half of the container-responsive contract. Prefer a CSS
 * container query — see `useAppPaneQueryContainer` — and reach for this hook
 * only where the responsive decision cannot be expressed in CSS.
 */
export function useAppPaneWidth(): number | null {
  return useContext(AppPaneWidthContext)
}

/**
 * Opt the application pane into being a CSS SIZE query container for as long
 * as the calling component is mounted.
 *
 * ## Why this is opt-in rather than always-on
 *
 * `container-type: inline-size` applies **layout containment**, and layout
 * containment makes the element a containing block for every `position: fixed`
 * descendant. The application pane is an ancestor of the entire route tree,
 * and this repo has ~30 components that render a non-portaled `fixed` overlay
 * under it (`drawer.tsx`, `command-bar.tsx`, `keyboard-shortcuts-drawer.tsx`,
 * the reflection and detail-panel overlays, …). With the container always on,
 * every one of those would be positioned against a document-tall box instead
 * of against the viewport, so a drawer opened at scroll offset 800px would
 * paint at the top of the document. That is a regression on every route,
 * present whether or not the chat is even open — and #12946's own brief
 * requires that "fixed global modals and portals continue to target the
 * browser viewport".
 *
 * So the container is NAMED unconditionally (a name alone has no layout
 * effect) and TYPED on request. A route migrating to container-responsive
 * layout calls this hook once, after it has portaled or repositioned any
 * viewport-fixed overlay it owns. Ref-counted, so two migrated subtrees on one
 * route do not fight.
 *
 * Contract and migration recipe: docs/conventions/APP_FRAME.md.
 */
export function useAppPaneQueryContainer(): void {
  const { addQueryContainerRequest, removeQueryContainerRequest } =
    useAppFrame()

  useEffect(() => {
    addQueryContainerRequest()
    return () => removeQueryContainerRequest()
  }, [addQueryContainerRequest, removeQueryContainerRequest])
}
